// src/components/templates/organisation/OrganisationDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Building2, ClipboardList, Activity, Package, Database,
  Calendar, BarChart3, PieChart, ArrowUp, ArrowDown, AlertCircle, CheckCircle,
  AlertTriangle, Clock, Target, DollarSign, Timer, Zap, MapPin
} from 'lucide-react';
import { organisationService } from '../../../services/organisationService';

export const OrganisationDashboard = ({ 
  bookData, 
  formData, 
  managementConfig, 
  managementFeatures = {},
  enabledFeatures, 
  onNavigate 
}) => {
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    loadDashboardData();
  }, [bookData?.id, timeRange, managementFeatures]);

  const loadDashboardData = async () => {
    if (!bookData?.id) return;

    try {
      setLoading(true);
      
      // Use the managementFeatures data passed from OrganisationPortal
      setDashboardData(managementFeatures);
      
      // Calculate analytics
      const analyticsData = calculateAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = () => {
    const recordsData = managementFeatures.records || {};
    const activities = recordsData.activities || [];
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - parseInt(timeRange) * 24 * 60 * 60 * 1000);
    
    const periodActivities = activities.filter(activity => {
      const activityDate = activity.createdAt?.toDate ? activity.createdAt.toDate() : new Date(activity.createdAt);
      return activityDate >= cutoffDate;
    });

    // Status distribution
    const statusCounts = {
      green: periodActivities.filter(a => a.status === 'green').length,
      orange: periodActivities.filter(a => a.status === 'orange').length,
      red: periodActivities.filter(a => a.status === 'red').length
    };

    // Activity type distribution
    const typeCounts = {};
    periodActivities.forEach(activity => {
      typeCounts[activity.activityType] = (typeCounts[activity.activityType] || 0) + 1;
    });

    // Time-based analytics
    const totalHours = periodActivities.reduce((sum, activity) => {
      return sum + (activity.metrics?.actualDuration || 0);
    }, 0) / 60; // Convert to hours

    // Quality analytics
    const qualityScores = periodActivities
      .filter(activity => activity.enabledMetrics?.quality && activity.metrics?.qualityScore)
      .map(activity => parseFloat(activity.metrics.qualityScore));
    
    const averageQuality = qualityScores.length > 0 
      ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
      : 0;

    // Financial analytics
    const financialActivities = periodActivities.filter(activity => 
      activity.enabledMetrics?.amount && activity.metrics?.amountValue
    );
    
    const totalCosts = financialActivities
      .filter(activity => activity.metrics.amountType === 'cost')
      .reduce((sum, activity) => sum + parseFloat(activity.metrics.amountValue || 0), 0);
    
    const totalIncome = financialActivities
      .filter(activity => activity.metrics.amountType === 'income')
      .reduce((sum, activity) => sum + parseFloat(activity.metrics.amountValue || 0), 0);
    
    const totalSavings = financialActivities
      .filter(activity => activity.metrics.amountType === 'saving')
      .reduce((sum, activity) => sum + parseFloat(activity.metrics.amountValue || 0), 0);

    // Progress analytics
    const progressActivities = periodActivities.filter(activity => 
      activity.enabledMetrics?.progress && activity.metrics?.completionPercentage !== undefined
    );
    
    const averageProgress = progressActivities.length > 0
      ? progressActivities.reduce((sum, activity) => sum + activity.metrics.completionPercentage, 0) / progressActivities.length
      : 0;

    // Daily activity trend
    const dailyTrend = {};
    periodActivities.forEach(activity => {
      const date = (activity.createdAt?.toDate ? activity.createdAt.toDate() : new Date(activity.createdAt)).toDateString();
      dailyTrend[date] = (dailyTrend[date] || 0) + 1;
    });

    return {
      total: periodActivities.length,
      statusCounts,
      typeCounts,
      totalHours: Math.round(totalHours * 10) / 10, // Round to 1 decimal
      averageQuality: Math.round(averageQuality * 10) / 10,
      financialSummary: {
        costs: totalCosts,
        income: totalIncome,
        savings: totalSavings,
        netImpact: totalIncome + totalSavings - totalCosts
      },
      averageProgress,
      dailyTrend,
      completionRate: periodActivities.filter(a => a.metrics?.completionPercentage === 100).length / periodActivities.length * 100 || 0
    };
  };

  const getModuleStats = () => {
    const stats = {};
    
    enabledFeatures.forEach(feature => {
      const featureData = managementFeatures[feature] || {};
      
      switch (feature) {
        case 'team':
          stats.team = {
            total: featureData.totalMembers || featureData.members?.length || 0,
            active: featureData.activeMembers || featureData.members?.filter(m => m.isActive)?.length || 0,
            trend: '+5.2%',
            color: 'blue',
            icon: Users
          };
          break;
        case 'assets':
          stats.assets = {
            total: featureData.totalAssets || featureData.assets?.length || 0,
            active: featureData.activeAssets || featureData.assets?.filter(a => a.status === 'active')?.length || 0,
            trend: '+2.1%',
            color: 'green',
            icon: Building2
          };
          break;
        case 'tasks':
          stats.tasks = {
            total: featureData.totalTasks || featureData.tasks?.length || 0,
            completed: featureData.completedTasks || featureData.tasks?.filter(t => t.status === 'completed')?.length || 0,
            trend: '+12.3%',
            color: 'orange',
            icon: ClipboardList
          };
          break;
        case 'sites':
          stats.sites = {
            total: featureData.totalSites || featureData.sites?.length || 0,
            active: featureData.activeSites || featureData.sites?.filter(s => s.status === 'active')?.length || 0,
            trend: '+3.7%',
            color: 'pink',
            icon: MapPin
          };
          break;
        case 'clients':
          stats.clients = {
            total: featureData.totalClients || featureData.clients?.length || 0,
            active: featureData.activeClients || featureData.clients?.filter(c => c.status === 'active')?.length || 0,
            trend: '+8.1%',
            color: 'indigo',
            icon: Users
          };
          break;
        case 'records':
          stats.records = {
            total: analytics.total || 0,
            active: analytics.statusCounts?.green || 0,
            trend: '+15.4%',
            color: 'gray',
            icon: Database
          };
          break;
      }
    });

    return stats;
  };

  const StatusChart = ({ data }) => (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Status Distribution</h3>
      <div className="space-y-4">
        {Object.entries(data).map(([status, count]) => {
          const config = {
            green: { label: 'On Track', color: 'bg-green-500', textColor: 'text-green-700' },
            orange: { label: 'Needs Attention', color: 'bg-orange-500', textColor: 'text-orange-700' },
            red: { label: 'Critical Issues', color: 'bg-red-500', textColor: 'text-red-700' }
          };
          
          const statusConfig = config[status];
          const total = Object.values(data).reduce((sum, val) => sum + val, 0);
          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
          
          return (
            <div key={status} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${statusConfig.color}`}></div>
                <span className="text-sm text-gray-600">{statusConfig.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-medium ${statusConfig.textColor}`}>
                  {count}
                </div>
                <div className="text-xs text-gray-500">({percentage}%)</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const ActivityTypeChart = ({ data }) => (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Types</h3>
      <div className="space-y-3">
        {Object.entries(data).slice(0, 5).map(([type, count]) => (
          <div key={type} className="flex items-center justify-between">
            <span className="text-sm text-gray-600 capitalize">{type.replace('_', ' ')}</span>
            <span className="text-sm font-medium text-gray-900">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStatCard = (title, value, subtitle, trend, color, Icon, onClick) => (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 bg-${color}-100 rounded-lg`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className={`flex items-center text-sm ${
          trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
        }`}>
          {trend.startsWith('+') ? <ArrowUp className="h-4 w-4 mr-1" /> : <ArrowDown className="h-4 w-4 mr-1" />}
          {trend}
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-500">{title}</div>
      {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
    </div>
  );

  const PerformanceMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Hours */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Clock className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-1">{analytics.totalHours}h</div>
        <div className="text-sm text-gray-500">Total Hours Logged</div>
        <div className="text-xs text-gray-400 mt-1">Last {timeRange} days</div>
      </div>

      {/* Average Quality */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Target className="h-6 w-6 text-green-600" />
          </div>
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-1">{analytics.averageQuality}/10</div>
        <div className="text-sm text-gray-500">Average Quality Score</div>
        <div className="text-xs text-gray-400 mt-1">Quality-tracked activities</div>
      </div>

      {/* Financial Impact */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <DollarSign className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <div className={`text-2xl font-bold mb-1 ${
          analytics.financialSummary?.netImpact >= 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          R{Math.abs(analytics.financialSummary?.netImpact || 0).toLocaleString()}
        </div>
        <div className="text-sm text-gray-500">Net Financial Impact</div>
        <div className="text-xs text-gray-400 mt-1">
          {analytics.financialSummary?.netImpact >= 0 ? 'Positive' : 'Negative'} impact
        </div>
      </div>

      {/* Completion Rate */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 bg-orange-100 rounded-lg">
            <BarChart3 className="h-6 w-6 text-orange-600" />
          </div>
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-1">{Math.round(analytics.completionRate)}%</div>
        <div className="text-sm text-gray-500">Completion Rate</div>
        <div className="text-xs text-gray-400 mt-1">100% completed activities</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map(i => (
              <div key={i} className="bg-gray-200 h-64 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const moduleStats = getModuleStats();

  return (
    <div className="p-6 space-y-8">
      {/* Dashboard Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Management Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of organizational performance and activities</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Module Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Object.entries(moduleStats).map(([key, stat]) => {
          const Icon = stat.icon;
          return renderStatCard(
            key.charAt(0).toUpperCase() + key.slice(1),
            key === 'tasks' ? stat.completed : stat.active || stat.total,
            key === 'tasks' ? `${stat.completed}/${stat.total} completed` : `${stat.active || 0}/${stat.total} total`,
            stat.trend,
            stat.color,
            Icon,
            () => onNavigate(key)
          );
        })}
      </div>

      {/* Performance Metrics */}
      <PerformanceMetrics />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatusChart data={analytics.statusCounts || {}} />
        <ActivityTypeChart data={analytics.typeCounts || {}} />
      </div>

      {/* Detailed Financial Breakdown */}
      {analytics.financialSummary && Object.values(analytics.financialSummary).some(val => val > 0) && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                R{analytics.financialSummary.costs.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Costs</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                R{analytics.financialSummary.income.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Income</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                R{analytics.financialSummary.savings.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Savings</div>
            </div>
            <div className={`text-center p-4 rounded-lg ${
              analytics.financialSummary.netImpact >= 0 ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className={`text-2xl font-bold ${
                analytics.financialSummary.netImpact >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {analytics.financialSummary.netImpact >= 0 ? '+' : ''}R{analytics.financialSummary.netImpact.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Net Impact</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};